
import React, { useState, useEffect } from "react";
import { StudyNote } from "@/types/notes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, FileText, Languages, ChevronLeft } from "lucide-react";
import { format } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { AdaptiveNoteContent } from './AdaptiveNoteContent';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from "@/components/ui/scroll-area";

interface StudyNoteViewerProps {
  note: StudyNote;
  onBack?: () => void;
}

export const StudyNoteViewer: React.FC<StudyNoteViewerProps> = ({ note, onBack }) => {
  const { isMobile } = useIsMobile();
  const [fontSize, setFontSize] = useState<'normal' | 'large' | 'larger'>('normal');
  const [mounted, setMounted] = useState(false);
  
  // Add effect to ensure component is mounted properly
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Function to safely render content based on contentType
  const renderContent = () => {
    if (!note || !mounted) return null;
    
    if (note.contentType === "html") {
      return (
        <div className="w-full mx-auto">
          <AdaptiveNoteContent content={note.content} />
        </div>
      );
    } else {
      return (
        <div className={`prose ${fontSize === 'normal' ? 'prose-sm' : fontSize === 'large' ? 'prose-md' : 'prose-lg'} mx-auto dark:prose-invert`}>
          <div className={`${isMobile ? 'text-sm' : 'text-lg'}`} dangerouslySetInnerHTML={{ __html: note.content.replace(/\n/g, '<br />') }} />
        </div>
      );
    }
  };

  const getLanguageLabel = (langCode?: string) => {
    switch (langCode) {
      case 'hi':
        return 'Hindi';
      case 'en':
        return 'English';
      default:
        return 'English';
    }
  };

  const getLanguageColor = (langCode?: string) => {
    switch (langCode) {
      case 'hi':
        return 'bg-secondary/20 text-secondary';
      case 'en':
        return 'bg-primary/20 text-primary';
      default:
        return 'bg-primary/20 text-primary';
    }
  };

  if (!note) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-sm">Select a note to view</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full w-full">
      <div className="sticky top-0 z-10 bg-white shadow-sm py-1 px-2 mb-2">
        <div className="flex flex-col gap-1">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-1">
              {onBack && (
                <Button variant="ghost" size="sm" onClick={onBack} className="h-6 p-1">
                  <ChevronLeft className="h-3 w-3" />
                  <span className="sr-only">Back</span>
                </Button>
              )}
              <h1 className="text-xs font-medium line-clamp-1">{note.title}</h1>
            </div>
            
            <div className="flex items-center gap-1">
              <div className="flex gap-0.5">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFontSize('normal')} 
                  className={`h-5 px-1 text-[10px] ${fontSize === 'normal' ? 'bg-secondary/10' : ''}`}
                >
                  A
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFontSize('large')} 
                  className={`h-5 px-1 text-xs ${fontSize === 'large' ? 'bg-secondary/10' : ''}`}
                >
                  A
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFontSize('larger')} 
                  className={`h-5 px-1 text-sm ${fontSize === 'larger' ? 'bg-secondary/10' : ''}`}
                >
                  A
                </Button>
              </div>
              
              {note.fileUrl && (
                <Button variant="outline" size="sm" asChild className="h-6 shrink-0 ml-1">
                  <a href={note.fileUrl} download target="_blank" rel="noopener noreferrer">
                    <Download className="h-3 w-3" />
                  </a>
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <Badge variant="outline" className="capitalize text-[10px] font-normal py-0 px-1 h-4">
              {note.category}
            </Badge>
            <Badge variant="secondary" className="capitalize text-[10px] font-normal py-0 px-1 h-4">
              {note.subject}
            </Badge>
            <Badge className={`capitalize text-[10px] font-normal py-0 px-1 h-4 ${getLanguageColor(note.language)}`}>
              <Languages className="h-2 w-2 mr-0.5" />
              {getLanguageLabel(note.language)}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {format(new Date(note.createdAt), "MMM d, yyyy")}
            </span>
          </div>
        </div>
      </div>

      <div className="px-2 pb-8">
        <div className="text-sm leading-relaxed">
          {renderContent()}
        </div>
        
        {note.fileUrl && (
          <div className="mt-3 border rounded-lg p-2 flex items-center justify-between bg-gray-50/70 shadow-sm">
            <div className="flex items-center">
              <FileText className="h-3 w-3 mr-1.5 text-primary" />
              <div>
                <h4 className="font-medium text-[10px]">Attached File</h4>
                <p className="text-[8px] text-muted-foreground truncate max-w-[100px]">
                  {note.fileUrl.substring(note.fileUrl.lastIndexOf('/') + 1)}
                </p>
              </div>
            </div>
            <Button variant="default" size="sm" asChild className="h-6 text-[10px]">
              <a href={note.fileUrl} download target="_blank" rel="noopener noreferrer">
                <Download className="mr-1 h-2.5 w-2.5" />
                Download
              </a>
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};
